import {
  Box,
  GridLegacy as Grid,
  type Theme,
  type CSSObject,
  Typography,
  styled,
  useMediaQuery,
} from '@mui/material';
import { colors, getCmsOptimizedUrl, NavLink } from '@rumblefish/ui';
import { Section, NavigationButton } from '@rumblefish/ui/Rumblefish23Theme';
import { motion, type PanInfo } from 'framer-motion';
import React, { useState } from 'react';
import Background from '../../../../public/webp/fromPNG/services/dots.webp';
import type { CaseStudyBlockProps } from './CaseStudyBlock.types';
const PREFIX = 'CaseStudyBlock';

const classes = {
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  container: `${PREFIX}-container`,
  containerWithBg: `${PREFIX}-containerWithBg`,
  logoContainer: `${PREFIX}-logoContainer`,
  logo: `${PREFIX}-logo`,
  headerImage: `${PREFIX}-headerImage`,
  button: `${PREFIX}-button`,
  arrowRight: `${PREFIX}-arrowRight`,
  alignContentLeft: `${PREFIX}-alignContentLeft`,
  alignContentCenter: `${PREFIX}-alignContentCenter`,
  content: `${PREFIX}-content`,
  technologyContainer: `${PREFIX}-technologyContainer`,
  technology: `${PREFIX}-technology`,
  technologies: `${PREFIX}-technologies`,
};

const CaseStudySection = styled(Section)(() => ({
  position: 'relative',
}));

const StyledFrame = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid rgba(255, 255, 255, 0.08)`,
    borderRadius: '20px',
    boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.06), 0 0 24px 4px rgba(255, 255, 255, 0.04)`,
    marginBottom: theme.spacing(10),
    overflow: 'hidden',
    padding: theme.spacing(3, 6),
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'scale(1.015)',
      boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.18), 0 0 40px 8px rgba(255, 255, 255, 0.08)`,
    },
  },
}));

const CaseStudyBlockWrapper = styled(motion.div)<{ offsetTop?: number }>(
  ({ offsetTop = 71, theme }) => ({
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      minHeight: `calc(100vh - ${offsetTop}px - 30px - 65px)`,
      margin: '15px',
      display: 'flex',
      alignItems: 'center',
    },
    [`& .${classes.subtitle}`]: {
      ...(theme.mixins.neutralGradient as CSSObject),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      [theme.breakpoints.down('md')]: {
        fontSize: '16px',
      },
    },

    [`& .${classes.title}`]: {
      ...(theme.mixins.neutralGradient as CSSObject),
      cursor: 'pointer',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      fontFamily: 'ClashDisplay',
      fontSize: '52px',
      fontStyle: 'normal',
      fontWeight: 900,
      lineHeight: '64px',
      letterSpacing: '-0.02em',
      textAlign: 'left',
      marginTop: '8px',
      marginBottom: '16px',

      [theme.breakpoints.down('lg')]: {
        textAlign: 'center',
        marginBottom: '0',
      },

      [theme.breakpoints.down('md')]: {
        fontSize: '26px',
        lineHeight: '32px',
      },
    },

    [`& .${classes.container}`]: {
      padding: '50px 0px 0px',
      [theme.breakpoints.down('md')]: {
        padding: '0px 0px',
      },
    },

    [`& .${classes.containerWithBg}`]: {
      padding: '50px 0px 20px',
      backgroundImage: `url(${Background})`,
      backgroundSize: 'cover',
      backgroundPosition: 'top',
      backgroundRepeat: 'no-repeat',
      [theme.breakpoints.down('md')]: {
        padding: '0px 0px',
      },
    },

    [`& .${classes.logoContainer}`]: {
      display: 'flex',
      margin: 'auto',
      padding: '20px',
      justifyContent: 'center',
      borderRadius: '10px',
      [theme.breakpoints.down('md')]: {
        padding: '10px',
      },
    },

    [`& .${classes.logo}`]: {
      maxHeight: '75px',
      height: 'auto',
      maxWidth: '200px',
      textAlign: 'center',
      objectContain: 'contain',
      [theme.breakpoints.down('md')]: {
        maxHeight: '60px',
      },
    },

    [`& .${classes.headerImage}`]: {
      width: '100%',
      objectFit: 'cover',
      objectPosition: 'top center',
      maxWidth: '550px',
      maxHeight: '300px',
      [theme.breakpoints.up('lg')]: {
        maxHeight: '420px',
      },
      [theme.breakpoints.up('xl')]: {
        maxHeight: '400px',
      },
    },
    [`& .${classes.button}`]: {
      marginTop: '30px',
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginBottom: 0,
      },
    },

    [`& .${classes.button}:hover`]: {
      boxShadow: `0px 0px 6px 4px ${theme.palette.raspberryColors?.PLUS_1}, 0px 0px 6px 4px ${theme.palette.raspberryColors?.PLUS_1} inset !important`,
    },

    [`& .${classes.arrowRight}`]: {
      display: 'inline-block',
      paddingLeft: '10px',
      width: '8px',
      height: '16px',
    },

    [`& .${classes.alignContentLeft}`]: {
      textAlign: 'left',
    },

    [`& .${classes.alignContentCenter}`]: {
      textAlign: 'center',
    },

    [`& .${classes.content}`]: {
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '19px',
      letterSpacing: '0em',
      textAlign: 'left',
      color: colors.mediumgray500,
      [theme.breakpoints.down('lg')]: {
        textAlign: 'center',
      },
    },

    [`& .${classes.technologyContainer}`]: {
      textAlign: 'left',
      paddingTop: '10px',
      [theme.breakpoints.down('lg')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.down('md')]: {
        paddingTop: 0,
      },
    },

    [`& .${classes.technology}`]: {
      display: 'inline',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '17px',
      letterSpacing: '0em',
      textAlign: 'left',
      color: colors.mediumgray400,
      [theme.breakpoints.down('lg')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
      },
    },

    [`& .${classes.technologies}`]: {
      display: 'inline',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 900,
      lineHeight: '17px',
      letterSpacing: '0em',
      textAlign: 'left',
      color: colors.mediumgray400,
      textWrap: 'wrap',
      [theme.breakpoints.down('lg')]: {
        textAlign: 'center',
      },
    },
  })
);

const StyledImage = styled(`img`)(({ theme }) => ({
  objectFit: 'contain',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const CaseStudyBlock: React.FC<CaseStudyBlockProps> = ({
  direction = 'row',
  caseStudy,
  offsetTop = 71,
  onSwipeDown,
  onSwipeUp,
}) => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const lgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const shouldShowLogo = caseStudy?.slug !== 'ai-chat-assistant';
  const [upExit, setUpExit] = useState<boolean>(true);

  const exit = upExit
    ? { opacity: 0, scale: 0.8, y: '-50vw' }
    : { opacity: 0, scale: 0.8, y: '50vw' };

  const handleSwipeDown = () => {
    setUpExit(false);
    onSwipeDown && onSwipeDown();
  };

  const handleSwipeUp = () => {
    setUpExit(true);
    onSwipeUp && onSwipeUp();
  };

  const mobileBlockProps = mdDown
    ? {
        offsetTop: offsetTop,
        drag: 'y' as const,
        dragConstraints: { top: 0, bottom: 0 },
        onDragEnd: (
          _event: MouseEvent | TouchEvent | PointerEvent,
          info: PanInfo
        ) => {
          if (info.offset.y < -80) {
            handleSwipeUp();
          }
          if (info.offset.y > 80) {
            handleSwipeDown();
          }
        },
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        exit: exit,
        transition: { duration: 0.3 },
      }
    : {};

  return (
    <CaseStudyBlockWrapper {...mobileBlockProps}>
      <CaseStudySection width="normal">
        <StyledFrame>
        <Grid
          container
          direction={direction}
          justifyContent="space-between"
          alignItems={'center'}
          className={
            direction === 'row' ? classes.container : classes.containerWithBg
          }>
          <Grid
            item
            className={
              (classes.alignContentLeft,
              lgDown || mdDown
                ? classes.alignContentCenter
                : classes.alignContentLeft)
            }
            xs={12}
            md={12}
            lg={6}>
            <Typography
              component="div"
              className={classes.subtitle}
              variant="h6">
              {caseStudy?.subtitle}
            </Typography>
            <NavLink to={`/case-studies/${caseStudy?.slug}/`}>
              <Typography
                component="div"
                className={classes.title}
                variant="h2">
                {caseStudy?.title}
              </Typography>
            </NavLink>

            {shouldShowLogo && lgDown && (
              <Box
                className={classes.logoContainer}
                display={{ xs: 'block', lg: 'none' }}>
                <img
                  className={classes.logo}
                  src={getCmsOptimizedUrl({
                    url: caseStudy?.logo?.url ? caseStudy.logo.url : '',
                    width: 400,
                  })}
                  alt={
                    caseStudy?.logo?.caption
                      ? caseStudy.logo.caption
                      : 'caseStudyLogo'
                  }
                />
              </Box>
            )}
            <div className={classes.technologyContainer}>
              <Typography className={classes.content} variant="body1">
                {caseStudy?.cardDescription}
              </Typography>
            </div>
            <NavigationButton
              className={classes.button}
              target={`/case-studies/${caseStudy?.slug}/`}
              buttonText="Read more"
              color="raspberry"
              ariaLabel={caseStudy?.title?.toString()}></NavigationButton>
          </Grid>
          <Grid
            item
            container
            lg={5}
            xl={6}
            justifyContent="center"
            alignItems="center">
            <StyledImage
              className={classes.headerImage}
              src={getCmsOptimizedUrl({
                url: caseStudy?.image?.url ? caseStudy.image?.url : '',
                width: 1100,

                quality: 60,
              })}
              alt={caseStudy?.image?.caption ? caseStudy.image.caption : ''}
            />
          </Grid>
        </Grid>
        </StyledFrame>
      </CaseStudySection>
    </CaseStudyBlockWrapper>
  );
};
